<template>
  <div class="lp-element-panel lp-address-panel">
    <field-group label="Position on page">
      <position-controls
          :horizontal="shownAlignment.horizontal"
          :vertical="shownAlignment.vertical"
          @align="onPositionAlign"/>
    </field-group>

    <field-group label="Effects">
      <effect-tiles
          :options="addressEffectOptions"
          :value="addressEffectValue"
          :columns="3"
          @select="onAddressEffectSelect"/>
      <effect-control-card
          v-if="addressEffectValue === 'outline'"
          :rows="imageOutlineRows"
          :color="tempImageOutlineColor"
          class="lp-address-panel__effect-card"
          color-aria="Open outline color picker"
          show-color
          @input="onImageOutlineInput"
          @pick="openImageEffectColorPicker"/>
      <effect-control-card
          v-if="addressEffectValue === 'shadow'"
          :rows="imageShadowRows"
          :color="tempImageShadowColor"
          class="lp-address-panel__effect-card"
          color-aria="Open shadow color picker"
          show-color
          @input="onImageShadowInput"
          @pick="openImageEffectColorPicker"/>
    </field-group>
    <side-popover
        ref="imageEffectColorPopoverEl"
        :open="isColorPickerOpen && (addressEffectValue === 'outline' || addressEffectValue === 'shadow')"
        :top="imageEffectColorPopover.top"
        :left="imageEffectColorPopover.left"
        :width="303"
        :title="addressEffectValue === 'outline' ? 'Outline color' : 'Shadow color'"
        @close="isColorPickerOpen = false"
        @click.native.stop>
      <color-picker
          v-if="addressEffectValue === 'outline'"
          :key="'address-outline-color'"
          :color="tempImageOutlineColor"
          :type="getObjectType"
          :active-object="activeObjectForLeftPanel"
          :selected-objects-list="getSelectedObjectsList"
          :content-list="contentList"
          :show-gradient="false"
          display-style="inline"
          variant="panel"
          color-type="outline"/>
      <color-picker
          v-else
          :key="'address-shadow-color'"
          :active-object="activeObjectForLeftPanel"
          :color="tempImageShadowColor"
          :type="getObjectType"
          :is-for-object-shadow-effect="true"
          :selected-objects-list="getSelectedObjectsList"
          :content-list="contentList"
          :show-gradient="false"
          display-style="inline"
          variant="panel"
          color-type="shadow"/>
    </side-popover>

    <field-group
        v-if="!isMultiPropertySelection"
        label="Address box designs">
      <div class="lp-address-designs">
        <button
            v-for="(AddressBoxes, i) in productAddressBoxes"
            :key="'address-'+i"
            :class="{ 'is-on': isCurrentAddressBox(AddressBoxes), 'is-white': AddressBoxes.isWhite }"
            :aria-pressed="isCurrentAddressBox(AddressBoxes) ? 'true' : 'false'"
            :aria-label="'Address box design ' + (i + 1)"
            type="button"
            class="lp-address-design"
            @click.stop="replaceAddressBoxImage(AddressBoxes)">
          <img
              :src="AddressBoxes.DefaultImagePath"
              alt="">
        </button>
      </div>
    </field-group>
  </div>
</template>

<script>
import EventBus from '@/EventBus';
import ColorPicker from '@/components/ColorPicker/ColorPicker';
import panelContext from '@/components/LeftSection/mixins/panelContext';
import objectAlignment from '@/components/LeftSection/mixins/objectAlignment';
import objectEffects from '@/components/LeftSection/mixins/objectEffects';
import imageEffects from '@/components/LeftSection/mixins/imageEffects';
import sidePopover from '@/components/LeftSection/mixins/sidePopover';
import FieldGroup from '@/components/LeftSection/ui/FieldGroup';
import PositionControls from '@/components/LeftSection/ui/PositionControls';
import EffectTiles from '@/components/LeftSection/ui/EffectTiles';
import EffectControlCard from '@/components/LeftSection/ui/EffectControlCard';
import SidePopover from '@/components/LeftSection/ui/SidePopover';

const ADDRESS_EFFECT_OPTIONS = [
  {value: 'none', label: 'None', preview: 'none'},
  {value: 'outline', label: 'Outline', preview: 'outline'},
  {value: 'shadow', label: 'Shadow', preview: 'shadow'},
];

export default {
  name: 'AddressPanel',
  components: {
    'color-picker': ColorPicker,
    'field-group': FieldGroup,
    'position-controls': PositionControls,
    'effect-tiles': EffectTiles,
    'effect-control-card': EffectControlCard,
    'side-popover': SidePopover,
  },
  mixins: [panelContext, objectAlignment, objectEffects, imageEffects, sidePopover],
  props: {
    width: {type: Number, default: 0},
    height: {type: Number, default: 0},
    productCrop: {type: Object, default: null},
    productAddressBoxes: {type: Array, default: () => []},
  },
  data() {
    return {
      addressEffectOptions: ADDRESS_EFFECT_OPTIONS,
    };
  },
  computed: {
    // Same source as the old radio-like tiles: the selected object's `effect`.
    addressEffectValue() {
      const effect = this.activeObjectForLeftPanel && this.activeObjectForLeftPanel.effect;
      return !effect || effect === 'none' ? 'none' : effect;
    },
  },
  methods: {
    onAddressEffectSelect(value) {
      if (value === 'outline') {
        this.applyOutlineOnAddress();
      } else if (value === 'shadow') {
        this.applyShadowOnImage();
      } else {
        this.removeImageEffect();
      }
    },
    // The canvas copies DefaultImagePath into the object's DefaultImageUrl when a design is picked.
    isCurrentAddressBox(addressBox) {
      return !!(addressBox
          && this.activeObjectForLeftPanel
          && addressBox.DefaultImagePath
          && this.activeObjectForLeftPanel.DefaultImageUrl === addressBox.DefaultImagePath);
    },
    replaceAddressBoxImage(AddressBoxesPath) {
      EventBus.$emit('onReplaceAddressBoxImage', AddressBoxesPath);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../styles/lp-tokens';
@import '../styles/lp-panel';

.lp-address-panel {
  padding: 4px 4px 8px;
}

.lp-address-panel__effect-card {
  margin-top: 10px;
}

.lp-address-designs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.lp-address-design {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 96px;
  padding: 16px 28px;
  border: 0;
  border-radius: $lp-radius-panel;
  background: $lp-fill;
  box-shadow: inset 0 0 0 1px $lp-line;
  cursor: pointer;
  transition: box-shadow 0.12s ease;

  img {
    display: block;
    width: 100%;
    max-height: 120px;
    object-fit: contain;
    pointer-events: none;
  }

  &:hover {
    box-shadow: inset 0 0 0 1px #c9ced6;
  }

  &:focus-visible {
    outline: 2px solid $lp-accent;
    outline-offset: 2px;
  }

  // White artwork needs a darker card so it stays visible (the old list used a grey background).
  &.is-white {
    background: #c9ced6;
  }

  &.is-on {
    box-shadow: inset 0 0 0 2px $lp-accent;
  }
}
</style>
